import React, { FC, useCallback } from 'react';
import { Form, Radio } from 'src/common';
import { useTranslation } from 'react-i18next';
import { SsoProvider } from 'src/models/users/types';
import {
  useSsoConfig,
  useCreateSsoConfig,
  useUpdateSsoConfig,
  useDeleteSsoConfig
} from 'src/models/company/Security/hooks';
import styles from './index.module.scss';
import { InfoLabel } from 'src/shared';
import modalStyles from 'src/common/Modal/index.module.scss';
import confirm from 'src/common/confirm';
import { IFormState } from '..';
import { FormInstance } from 'antd/lib/form';

interface IProps {
  form: FormInstance<IFormState>;
  currentSsoProvider: SsoProvider | undefined;
}

export const SsoProviderForm: FC<IProps> = ({ form, currentSsoProvider }) => {
  const { t } = useTranslation();
  const { isSsoConfigExist, emailDomain } = useSsoConfig();
  const [onCreateSsoConfig] = useCreateSsoConfig();
  const [onUpdateSsoConfig] = useUpdateSsoConfig();
  const [onDeleteSsoConfig] = useDeleteSsoConfig();

  const onValuesChange = useCallback(() => {
    const providerValue = form.getFieldValue('provider');

    if (providerValue === SsoProvider.microsoft || providerValue === currentSsoProvider) {
      return;
    }

    confirm.confirm({
      title: t('Security.ssoProvider'),
      content:
        currentSsoProvider === undefined ? t('Security.activateSSOProviderDesc') : t('Security.switchSSOProviderDesc'),
      okText: t('Common.continue'),
      cancelText: t('Common.cancel'),
      className: modalStyles.confirmModal,
      centered: true,
      onCancel() {
        form.setFieldsValue({ provider: currentSsoProvider });
      },
      onOk() {
        if (providerValue === undefined && emailDomain) {
          onDeleteSsoConfig(emailDomain);
        }
        if (providerValue === SsoProvider.google) {
          if (isSsoConfigExist) {
            onUpdateSsoConfig({
              enabled: true,
              enforceSSO: false,
              allowNewUserCreationFromSSO: false,
              provider: providerValue
            });
          } else {
            onCreateSsoConfig({
              provider: providerValue,
              enabled: true,
              allowNewUserCreationFromSSO: false
            });
          }
        }
      }
    });
  }, [
    emailDomain,
    form,
    t,
    currentSsoProvider,
    isSsoConfigExist,
    onCreateSsoConfig,
    onUpdateSsoConfig,
    onDeleteSsoConfig
  ]);

  return (
    <Form.Item
      name={'provider'}
      label={<InfoLabel title={t('Security.ssoProvider')} desc={t('Security.ssoProviderDesc')} />}
      className={styles.form}
    >
      <Radio.Group className={styles.group} onChange={onValuesChange}>
        <Radio value={undefined} className={styles.groupItem}>
          {t(`Security.ssoNotEnabled`)}
        </Radio>
        {Object.entries(SsoProvider).map(([key, value]) => (
          <Radio value={value} key={key} className={styles.groupItem}>
            {t(`Security.${key}Provider`)}
          </Radio>
        ))}
      </Radio.Group>
    </Form.Item>
  );
};

export default SsoProviderForm;
